// Finds recurring bills in the expense list and notices when the price goes up. Pure functions, no network.
// A "bill" = the same payee (from the note) charged on a regular rhythm: monthly, quarterly or yearly.

const DAY = 86400000;
const toDay = (iso) => Math.floor(Date.parse(`${iso}T00:00:00Z`) / DAY);
const median = (a) => { const s = [...a].sort((x, y) => x - y); const m = s.length >> 1; return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
const r2 = (n) => Math.round(n * 100) / 100;

// Bills people can realistically negotiate (used to sort and to suggest a draft).
const NEGOTIABLE = /internet|broadband|fibre|fiber|nbn|wifi|mobile|phone|cell|wireless|data plan|sim|tv|cable|satellite|insurance|insure|energy|electric|power|gas|utilit|subscription|stream|gym|membership|netflix|spotify|disney|prime|foxtel|sky|optus|telstra|vodafone|verizon|at&t|t-mobile|comcast|xfinity|spectrum|bt\b|virgin|rogers|bell|telus/i;

export function payeeKey(note, category) {
  const n = String(note || "").toLowerCase()
    .replace(/[0-9#*_/\\.,:;()\-]+/g, " ")
    .replace(/\b(payment|pmt|autopay|auto pay|bill|invoice|monthly|recurring|direct debit|dd|pos|purchase|card|visa|debit|credit|subscription)\b/g, " ")
    .replace(/\s+/g, " ").trim();
  if (n.length >= 3) return n;
  const c = String(category || "").toLowerCase().trim();
  return NEGOTIABLE.test(c) ? `cat:${c}` : "";   // category-only entries count only for obviously billed categories
}

function cadenceOf(gaps, count) {
  if (!gaps.length) return null;
  const m = median(gaps);
  const tight = (target, tol) => gaps.filter((g) => Math.abs(g - target) <= tol).length / gaps.length >= 0.7;
  if (m >= 25 && m <= 35 && count >= 3 && tight(30, 8)) return { name: "monthly", days: 30.4 };
  if (m >= 80 && m <= 100 && count >= 3 && tight(91, 14)) return { name: "quarterly", days: 91.3 };
  if (m >= 340 && m <= 390 && count >= 2 && tight(365, 30)) return { name: "yearly", days: 365 };
  return null;
}

// expenses: [{ date: 'YYYY-MM-DD', amount, note, category }]
export function detectBills(expenses, { today = new Date().toISOString().slice(0, 10), minRisePct = 3, minRiseAmount = 1 } = {}) {
  const groups = new Map();
  (expenses || []).forEach((e) => {
    if (!e || !(Number(e.amount) > 0) || !/^\d{4}-\d{2}-\d{2}$/.test(e.date || "")) return;
    const key = payeeKey(e.note, e.category);
    if (!key) return;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ date: e.date, day: toDay(e.date), amount: Number(e.amount), note: e.note || "", category: e.category || "" });
  });

  const nowDay = toDay(today);
  const bills = [];
  groups.forEach((rows, key) => {
    rows.sort((a, b) => a.day - b.day);
    // two charges on the same day (a refund and a re-charge, say) count once, keeping the later amount
    const items = rows.filter((r, i) => i === rows.length - 1 || rows[i + 1].day !== r.day);
    if (items.length < 2) return;
    const gaps = items.slice(1).map((r, i) => r.day - items[i].day);
    const cad = cadenceOf(gaps, items.length);
    if (!cad) return;

    const latest = items[items.length - 1];
    const prior = items.slice(0, -1).slice(-3).map((r) => r.amount);
    const baseline = median(prior);
    const change = latest.amount - baseline;
    const pct = baseline > 0 ? (change / baseline) * 100 : 0;
    const increased = pct >= minRisePct && change >= minRiseAmount;

    // when did the price last step up?
    let increaseDate = "";
    for (let i = items.length - 1; i > 0; i--) {
      if (items[i].amount > items[i - 1].amount * (1 + minRisePct / 100) && items[i].amount - items[i - 1].amount >= minRiseAmount) { increaseDate = items[i].date; break; }
    }
    const first = items[0];
    const label = (latest.note || "").trim() || latest.category || key;
    const active = nowDay - latest.day <= cad.days * 2.2;
    const perYear = latest.amount * (365 / cad.days);
    bills.push({
      key, label, category: latest.category, cadence: cad.name, cadenceDays: Math.round(cad.days), count: items.length,
      lastDate: latest.date, lastAmount: r2(latest.amount), previousAmount: r2(baseline), firstAmount: r2(first.amount),
      change: r2(change), changePct: Math.round(pct * 10) / 10, increased, increaseDate,
      totalRisePct: first.amount > 0 ? Math.round(((latest.amount - first.amount) / first.amount) * 1000) / 10 : 0,
      annualCost: r2(perYear), extraPerYear: increased ? r2(change * (365 / cad.days)) : 0,
      negotiable: NEGOTIABLE.test(`${label} ${latest.category}`), active,
      history: items.slice(-8).map((r) => ({ date: r.date, amount: r2(r.amount) })),
    });
  });

  return bills.sort((a, b) => (b.increased && b.active) - (a.increased && a.active) || b.extraPerYear - a.extraPerYear || b.negotiable - a.negotiable || b.annualCost - a.annualCost);
}

export const billAlertCount = (expenses, opts) => detectBills(expenses, opts).filter((b) => b.increased && b.active).length;
